import * as React from 'react'
import { createFileRoute, Link, Outlet, useRouterState } from '@tanstack/react-router'
import {
  ArrowLeft,
  Activity,
  CheckCircle2,
  CircleDot,
  ClipboardCheck,
  GitBranch,
  MessageSquareText,
  Save,
  Square,
  Tags,
  UserRoundCheck,
  UserPlus,
  Waypoints,
} from 'lucide-react'
import {
  useBosunKanbanSummary,
  useBosunKanbanTasks,
  useBosunProject,
  useBosunProjectActionItems,
  useBosunProjectGoals,
  useBosunProjectGoalsAudit,
  useResolveBosunProjectActionItem,
  useUpdateBosunProjectSettings,
  useUpdateBosunProjectGoals,
  useWorkspace,
  type BosunKanbanSummaryRead,
  type BosunKanbanTaskRead,
  type BosunProjectRead,
  type BosunUserActionItemRead,
} from '@/api/queries'
import { ProjectReleaseNotesPanel } from '@/components/ProjectReleaseNotesPanel'
import ErrorFallback from '@/components/ErrorFallback'
import { ProjectCockpitNav } from '@/components/ProjectCockpitNav'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { EmptyState } from '@/components/ui/empty-state'
import { Input, Textarea } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Progress } from '@/components/ui/progress'
import { Skeleton } from '@/components/ui/skeleton'
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { PageHeader, SectionHeader } from '@/components/ui/page-header'
import type { ProjectCockpitTab } from '@/components/ProjectCockpitNav'
import { canManageProjects } from '@/api/workspace-permissions'
import { formatError } from '@/lib/format-error'
import { formatLocalDateTime } from '@/lib/format-date'

const LazyPmChatPanel = React.lazy(() =>
  import('@/components/PmChatPanel').then((module) => ({
    default: module.PmChatPanel,
  })),
)

export const Route = createFileRoute('/_authenticated/projects/$workspaceUuid/$projectUuid')({
  component: ProjectGoalsPage,
  errorComponent: ErrorFallback,
})

type GoalGroup = 'active' | 'completed' | 'release-reviewed'
const PROJECT_TABS = new Set<ProjectCockpitTab>([
  'overview',
  'timeline',
  'kanban',
  'settings',
  'pm-chat',
  'action-items',
  'releases',
])

interface GoalItem {
  id: string
  text: string
  group: GoalGroup
}

const CHECKBOX_RE = /^\s*[-*]\s+\[([ xX])\]\s+(.+?)\s*$/
const RELEASE_REVIEWED_RE = /(^|\s)(#release-reviewed|\[release-reviewed\])(\s|$)/

function cleanGoalText(text: string): string {
  return text
    .replace(/\s*(#release-reviewed|\[release-reviewed\])\s*/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

function parseGoals(markdown: string): Record<GoalGroup, GoalItem[]> {
  const groups: Record<GoalGroup, GoalItem[]> = {
    active: [],
    completed: [],
    'release-reviewed': [],
  }
  markdown.split(/\r?\n/).forEach((line, index) => {
    const match = line.match(CHECKBOX_RE)
    if (!match) return
    const rawText = match[2] ?? ''
    const releaseReviewed = RELEASE_REVIEWED_RE.test(rawText)
    const group: GoalGroup = releaseReviewed
      ? 'release-reviewed'
      : match[1]?.toLowerCase() === 'x'
        ? 'completed'
        : 'active'
    groups[group].push({
      id: `${index}-${group}`,
      text: cleanGoalText(rawText),
      group,
    })
  })
  return groups
}

function auditRevision(metadata: unknown): string {
  if (!metadata || typeof metadata !== 'object' || !('revision' in metadata)) return '?'
  const revision = (metadata as { revision?: unknown }).revision
  return typeof revision === 'number' || typeof revision === 'string' ? String(revision) : '?'
}

function projectProgress(projectStatus: string, summary?: BosunKanbanSummaryRead): number {
  if (projectStatus === 'ready') return 100
  if (projectStatus === 'blocked' || (summary?.blocked_tasks.length ?? 0) > 0) return 45
  const active = openKanbanTaskCount(summary)
  if (projectStatus === 'running' || projectStatus === 'queued') return active > 0 ? 66 : 50
  if (projectStatus === 'failed') return 25
  return 15
}

function openKanbanTaskCount(summary?: BosunKanbanSummaryRead): number {
  return summary
    ? summary.counts.todo + summary.counts.waiting + summary.counts.in_progress + summary.counts.blocked
    : 0
}

function remainingActiveTasks(summary?: BosunKanbanSummaryRead): BosunKanbanSummaryRead['active_tasks'] {
  if (!summary) return []
  const specializedTaskIds = new Set([
    ...summary.running_tasks.map((task) => task.id),
    ...summary.blocked_tasks.map((task) => task.id),
    ...summary.waiting_tasks.map((task) => task.id),
  ])
  return summary.active_tasks.filter((task) => !specializedTaskIds.has(task.id))
}

function searchValue(search: unknown, key: string): string | null {
  if (typeof search === 'string') return new URLSearchParams(search).get(key)
  if (search && typeof search === 'object' && key in search) {
    const value = (search as Record<string, unknown>)[key]
    return typeof value === 'string' ? value : null
  }
  return null
}

function activeTabFromSearch(search: unknown): ProjectCockpitTab {
  const tab = searchValue(search, 'tab')
  return tab && PROJECT_TABS.has(tab as ProjectCockpitTab) ? (tab as ProjectCockpitTab) : 'overview'
}

function statusTone(status: string): React.ComponentProps<typeof Badge>['tone'] {
  if (status === 'blocked' || status === 'failed') return 'danger'
  if (status === 'in_progress' || status === 'running') return 'info'
  if (status === 'done' || status === 'completed' || status === 'ready') return 'success'
  if (status === 'waiting') return 'warning'
  return 'neutral'
}

function TaskList({
  title,
  tasks,
  tone,
  project,
}: {
  title: string
  tasks: BosunKanbanSummaryRead['active_tasks']
  tone: React.ComponentProps<typeof Badge>['tone']
  project: BosunProjectRead
}) {
  return (
    <Card tone="outline">
      <CardHeader>
        <div className="flex items-center justify-between gap-3">
          <CardTitle className="text-base">{title}</CardTitle>
          <Badge tone={tone}>{tasks.length}</Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-2 pt-4">
        {tasks.length ? (
          tasks.slice(0, 6).map((task) => (
            <Link
              key={task.id}
              to="/projects/$workspaceUuid/$projectUuid"
              params={{ workspaceUuid: project.workspace_uuid, projectUuid: project.uuid }}
              search={{ tab: 'kanban', task: task.id }}
              className="block w-full rounded-md border border-white/10 bg-white/[0.035] p-3 text-left text-sm hover:bg-white/[0.055]"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-medium">{task.title}</p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    {task.profile ?? task.assignee ?? task.owner ?? 'Unassigned'} · {task.raw_status}
                  </p>
                </div>
                <Badge tone={tone} size="xs">{task.status}</Badge>
              </div>
              {task.summary ? <p className="mt-2 text-xs text-muted-foreground">{task.summary}</p> : null}
              <dl className="mt-3 grid gap-2 text-xs text-muted-foreground sm:grid-cols-3">
                <div>
                  <dt className="font-medium text-foreground">Priority</dt>
                  <dd>{task.priority ?? 'n/a'}</dd>
                </div>
                <div>
                  <dt className="font-medium text-foreground">Run</dt>
                  <dd>{task.last_run_status ?? 'none'}</dd>
                </div>
                <div>
                  <dt className="font-medium text-foreground">Updated</dt>
                  <dd>{task.updated_at || task.last_event_at ? formatLocalDateTime(task.updated_at ?? task.last_event_at!) : 'No timestamp'}</dd>
                </div>
              </dl>
              <div className="mt-3 flex flex-wrap gap-2">
                {task.parent_tasks?.length ? <Badge tone="warning" size="xs">Parents {task.parent_tasks.length}</Badge> : null}
                {task.child_tasks?.length ? <Badge tone="info" size="xs">Children {task.child_tasks.length}</Badge> : null}
                {task.recent_events?.length ? <Badge tone="neutral" size="xs">Events {task.recent_events.length}</Badge> : null}
                {task.handoffs?.length ? <Badge tone="neutral" size="xs">Handoffs {task.handoffs.length}</Badge> : null}
              </div>
            </Link>
          ))
        ) : (
          <p className="text-sm text-muted-foreground">No tasks in this lane.</p>
        )}
      </CardContent>
    </Card>
  )
}

function TimelinePanel({
  project,
  summary,
}: {
  project: BosunProjectRead
  summary?: BosunKanbanSummaryRead
}) {
  const steps = project.provider_steps
  const stepRows = steps
    .filter((step) => {
      const key = String(step.key ?? '')
      return key.includes('bootstrap') || key.includes('credential') || key.includes('codex') || key.includes('generated_hermes') || key.includes('slack')
    })
    .map((step) => [
      String(step.label ?? step.key ?? 'Bootstrap step'),
      String(step.detail ?? step.todo ?? step.error ?? ''),
      String(step.status ?? 'pending'),
    ] as const)
  const openCount = openKanbanTaskCount(summary)
  const kanbanState = (summary?.blocked_tasks.length ?? 0) > 0 ? 'blocked' : openCount > 0 ? 'running' : project.status === 'blocked' ? 'blocked' : 'pending'
  const rows = [
    ['Launch request', project.created_at, 'completed'],
    ['Provisioning', project.blocked_reason ?? (project.status === 'ready' ? project.updated_at : ''), project.status],
    ...stepRows,
    ['Open PM/kanban work', openCount ? `${openCount} open` : project.blocked_reason ?? '', kanbanState],
    ...((summary?.recent_release_train_events ?? []).slice(0, 6).map((event) => [
      event.task_title ?? event.kind,
      event.summary ?? event.created_at ?? '',
      event.status ?? event.kind,
    ] as const)),
  ] as const
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Waypoints className="size-4 text-primary" />
          Launch timeline
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 pt-4">
        {rows.map(([label, detail, state]) => (
          <div key={label} className="grid items-start gap-2 rounded-md border border-white/10 bg-white/[0.025] p-3 sm:grid-cols-[minmax(8rem,12rem)_minmax(0,1fr)_auto] sm:items-center">
            <div className="flex min-w-0 items-center gap-2 font-medium">
              {state === 'completed' || state === 'ready' ? (
                <CheckCircle2 className="size-4 shrink-0 text-success" />
              ) : (
                <CircleDot className="size-4 shrink-0 text-muted-foreground" />
              )}
              <span className="min-w-0 break-words">{label}</span>
            </div>
            <p className="min-w-0 break-words text-sm text-muted-foreground">
              {detail ? (typeof detail === 'string' && detail.includes('T') ? formatLocalDateTime(detail) : detail) : 'Waiting for evidence'}
            </p>
            <Badge
              aria-label={`${label} status: ${state}`}
              className="justify-self-end self-center"
              size="xs"
              tone={state === 'blocked' || state === 'failed' ? 'danger' : state === 'completed' || state === 'ready' ? 'success' : state === 'running' ? 'info' : 'neutral'}
            >
              {state}
            </Badge>
          </div>
        ))}
        <Button asChild variant="secondary" size="sm">
          <Link to="/projects/$workspaceUuid/$projectUuid/provisioning" params={{ workspaceUuid: project.workspace_uuid, projectUuid: project.uuid }}>
            Raw provisioning facts
          </Link>
        </Button>
      </CardContent>
    </Card>
  )
}

function TaskDetailPanel({
  task,
}: {
  task: BosunKanbanTaskRead
}) {
  return (
    <Card tone="outline">
      <CardHeader>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <CardTitle role="heading" aria-level={2}>{task.title}</CardTitle>
          <Badge tone={statusTone(task.status)}>
            {task.raw_status}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-4 pt-4">
        <dl className="grid gap-3 text-sm md:grid-cols-3">
          <div>
            <dt className="text-muted-foreground">Assignee</dt>
            <dd>{task.assignee ?? task.profile ?? task.owner ?? 'Unassigned'}</dd>
          </div>
          <div>
            <dt className="text-muted-foreground">Priority</dt>
            <dd>{task.priority ?? 'n/a'}</dd>
          </div>
          <div>
            <dt className="text-muted-foreground">Current worker</dt>
            <dd>{task.current_worker ?? task.profile ?? task.assignee ?? 'Unassigned'}</dd>
          </div>
          <div>
            <dt className="text-muted-foreground">Heartbeat</dt>
            <dd>{task.last_heartbeat_at ? formatLocalDateTime(task.last_heartbeat_at) : 'No heartbeat captured'}</dd>
          </div>
          <div>
            <dt className="text-muted-foreground">Last event</dt>
            <dd>{task.last_event_at ? formatLocalDateTime(task.last_event_at) : 'No event captured'}</dd>
          </div>
        </dl>
        {task.summary ? <p className="text-sm text-muted-foreground">{task.summary}</p> : null}
        {task.body ? (
          <div className="space-y-2 rounded-md border border-white/10 bg-white/[0.025] p-3">
            <p className="text-sm font-medium">Ticket details</p>
            <p className="whitespace-pre-wrap text-sm text-muted-foreground">{task.body}</p>
          </div>
        ) : null}
        <div className="grid gap-3 md:grid-cols-2">
          <TaskChain title="Blocked by" tasks={task.parent_tasks} />
          <TaskChain title="Unblocks" tasks={task.child_tasks} />
        </div>
        <div className="grid gap-3 md:grid-cols-2">
          <TaskRuns task={task} />
          <TaskHandoffs task={task} />
        </div>
        <TaskEvents task={task} />
        <div className="space-y-2">
          <p className="text-sm font-medium">Progress comments</p>
          {(task.comments ?? []).length ? (
            (task.comments ?? []).map((comment, index) => (
              <p key={`${task.id}-comment-${index}`} className="rounded-md border border-white/10 bg-white/[0.025] p-3 text-sm text-muted-foreground">
                {comment}
              </p>
            ))
          ) : (
            <p className="text-sm text-muted-foreground">No comments captured yet.</p>
          )}
        </div>
      </CardContent>
    </Card>
  )
}

function TaskRuns({ task }: { task: BosunKanbanTaskRead }) {
  return (
    <div className="space-y-2 rounded-md border border-white/10 bg-white/[0.025] p-3">
      <p className="flex items-center gap-2 text-sm font-medium">
        <Activity className="size-4 text-primary" />
        Recent runs
      </p>
      {task.recent_runs?.length ? (
        task.recent_runs.map((run, index) => (
          <div key={run.id ?? `${task.id}-run-${index}`} className="space-y-1 text-sm">
            <div className="flex items-center justify-between gap-3">
              <span>{run.profile ?? 'worker'}</span>
              <Badge tone={statusTone(run.status ?? 'todo')} size="xs">{run.status ?? 'unknown'}</Badge>
            </div>
            {run.summary ? <p className="text-muted-foreground">{run.summary}</p> : null}
            <p className="text-xs text-muted-foreground">
              {run.started_at ? formatLocalDateTime(run.started_at) : 'No start time'}
              {run.worker ? ` · ${run.worker}` : ''}
            </p>
          </div>
        ))
      ) : (
        <p className="text-sm text-muted-foreground">No run history captured.</p>
      )}
    </div>
  )
}

function TaskEvents({ task }: { task: BosunKanbanTaskRead }) {
  return (
    <div className="space-y-2 rounded-md border border-white/10 bg-white/[0.025] p-3">
      <p className="flex items-center gap-2 text-sm font-medium">
        <GitBranch className="size-4 text-primary" />
        Recent events
      </p>
      {task.recent_events?.length ? (
        task.recent_events.map((event) => (
          <div key={event.id} className="grid gap-2 border-t border-white/10 py-2 first:border-t-0 first:pt-0 sm:grid-cols-[10rem_minmax(0,1fr)_7rem]">
            <div className="text-sm font-medium">{event.kind}</div>
            <p className="min-w-0 text-sm text-muted-foreground">{event.summary ?? event.profile ?? 'No event summary'}</p>
            <Badge tone={statusTone(event.status ?? task.status)} size="xs">{event.status ?? task.status}</Badge>
          </div>
        ))
      ) : (
        <p className="text-sm text-muted-foreground">No events captured.</p>
      )}
    </div>
  )
}

function TaskHandoffs({ task }: { task: BosunKanbanTaskRead }) {
  return (
    <div className="space-y-2 rounded-md border border-white/10 bg-white/[0.025] p-3">
      <p className="flex items-center gap-2 text-sm font-medium">
        <MessageSquareText className="size-4 text-primary" />
        Handoffs
      </p>
      {task.handoffs?.length ? (
        task.handoffs.map((handoff, index) => (
          <div key={`${task.id}-handoff-${index}`} className="space-y-1 text-sm">
            <p>{handoff.from_profile ?? 'unknown'} to {handoff.to_profile ?? 'unknown'}</p>
            {handoff.summary ? <p className="text-muted-foreground">{handoff.summary}</p> : null}
            <p className="text-xs text-muted-foreground">{handoff.created_at ? formatLocalDateTime(handoff.created_at) : 'No timestamp'}</p>
          </div>
        ))
      ) : (
        <p className="text-sm text-muted-foreground">No handoffs captured.</p>
      )}
    </div>
  )
}

function TaskChain({
  title,
  tasks = [],
}: {
  title: string
  tasks: BosunKanbanTaskRead['parent_tasks']
}) {
  return (
    <div className="space-y-2 rounded-md border border-white/10 bg-white/[0.025] p-3">
      <p className="text-sm font-medium">{title}</p>
      {tasks.length ? (
        tasks.map((task) => (
          <div key={task.task_id} className="flex items-center justify-between gap-3 text-sm">
            <span>{task.title ?? task.task_id}</span>
            <Badge tone={task.status === 'blocked' ? 'danger' : 'neutral'} size="xs">{task.status ?? 'unknown'}</Badge>
          </div>
        ))
      ) : (
        <p className="text-sm text-muted-foreground">None reported.</p>
      )}
    </div>
  )
}

function slackInviteInputValue(project: BosunProjectRead): string {
  return (project.slack_default_invite_user_ids ?? []).join(' ')
}

function slackInviteUserIds(value: string): string[] {
  const seen = new Set<string>()
  return value
    .replace(/,/g, ' ')
    .split(/\s+/)
    .map((entry) => entry.trim().toUpperCase())
    .filter(Boolean)
    .filter((entry) => {
      if (seen.has(entry)) return false
      seen.add(entry)
      return true
    })
}

function SlackInviteSettingsPanel({
  project,
  canEdit,
}: {
  project: BosunProjectRead
  canEdit: boolean
}) {
  const updateSettings = useUpdateBosunProjectSettings(project.workspace_uuid, project.uuid)
  const [draft, setDraft] = React.useState(slackInviteInputValue(project))

  const nextIds = slackInviteUserIds(draft)
  const dirty = nextIds.join(' ') !== slackInviteInputValue(project)

  async function save() {
    await updateSettings.mutateAsync({ slack_default_invite_user_ids: nextIds })
    setDraft(nextIds.join(' '))
  }

  return (
    <Card tone="outline">
      <CardHeader>
        <SectionHeader
          title="Slack invite users"
          actions={
            canEdit ? (
              <Button
                type="button"
                size="sm"
                onClick={() => void save()}
                disabled={!dirty || updateSettings.isPending}
              >
                <UserPlus />
                {updateSettings.isPending ? 'Applying' : 'Apply'}
              </Button>
            ) : undefined
          }
        />
      </CardHeader>
      <CardContent className="space-y-3 pt-4">
        {updateSettings.error ? (
          <Alert tone="danger">
            <AlertDescription>{formatError(updateSettings.error)}</AlertDescription>
          </Alert>
        ) : null}
        <div className="space-y-2">
          <Label htmlFor="slack-default-invite-user-ids">Default Slack invite user IDs</Label>
          <Input
            id="slack-default-invite-user-ids"
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            disabled={!canEdit}
            placeholder="U06G4CJ7F3L U012ABCDEF0"
            title="Enter one or more Slack user IDs such as U06G4CJ7F3L, separated by spaces or commas."
          />
        </div>
        <dl className="grid gap-2 text-sm text-muted-foreground sm:grid-cols-2">
          {Object.entries(project.slack_channel_map).map(([role, channel]) => (
            <div key={role} className="flex justify-between gap-3">
              <dt className="capitalize">{role}</dt>
              <dd>{channel || 'unbound'}</dd>
            </div>
          ))}
        </dl>
      </CardContent>
    </Card>
  )
}

function ActionItemsPanel({
  workspaceUuid,
  projectUuid,
  summary,
}: {
  workspaceUuid: string
  projectUuid: string
  summary?: BosunKanbanSummaryRead
}) {
  const actionItems = useBosunProjectActionItems(workspaceUuid, projectUuid)
  const resolveAction = useResolveBosunProjectActionItem(workspaceUuid, projectUuid)
  const [answers, setAnswers] = React.useState<Record<string, string>>({})
  const durableItems = actionItems.data ?? []
  const kanbanItems = [...(summary?.waiting_tasks ?? []), ...(summary?.blocked_tasks ?? [])]

  async function resolveItem(item: BosunUserActionItemRead, status: 'answered' | 'done') {
    await resolveAction.mutateAsync({
      actionItemUuid: item.uuid,
      body: {
        status,
        answer: answers[item.uuid]?.trim() || null,
        resolution: status === 'done' ? 'Resolved from project action-item inbox.' : null,
      },
    })
    setAnswers((current) => ({ ...current, [item.uuid]: '' }))
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <UserRoundCheck className="size-4 text-warning" />
          Waiting on you
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 pt-4">
        {actionItems.error ? (
          <Alert tone="danger">
            <AlertDescription>{formatError(actionItems.error)}</AlertDescription>
          </Alert>
        ) : null}
        {durableItems.length ? (
          durableItems.map((item) => {
            const isClosed = item.status === 'done' || item.status === 'dismissed'
            return (
              <div key={item.uuid} className="space-y-3 rounded-md border border-white/10 bg-white/[0.025] p-3">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-medium">{item.title}</p>
                    <p className="mt-1 text-sm text-muted-foreground">
                      {item.question ?? item.body ?? 'PM needs your input before this can continue.'}
                    </p>
                    {item.answer ? <p className="mt-2 text-sm">Answer: {item.answer}</p> : null}
                  </div>
                  <Badge tone={isClosed ? 'success' : item.status === 'open' ? 'warning' : 'info'}>{item.status}</Badge>
                </div>
                <Textarea
                  value={answers[item.uuid] ?? ''}
                  onChange={(event) => setAnswers((current) => ({ ...current, [item.uuid]: event.target.value }))}
                  placeholder="Answer or add the missing info..."
                  disabled={isClosed || resolveAction.isPending}
                />
                <div className="flex flex-wrap gap-2">
                  <Button type="button" size="sm" onClick={() => void resolveItem(item, 'answered')} disabled={isClosed || resolveAction.isPending}>
                    Answer
                  </Button>
                  <Button type="button" size="sm" variant="secondary" onClick={() => void resolveItem(item, 'done')} disabled={isClosed || resolveAction.isPending}>
                    Resolve
                  </Button>
                  {item.source_url ? (
                    <button
                      type="button"
                      onClick={() => window.open(item.source_url!, '_blank', 'noopener,noreferrer')}
                      className="inline-flex h-9 items-center rounded-md px-3 text-sm font-medium text-primary hover:underline"
                    >
                      Source
                    </button>
                  ) : null}
                </div>
              </div>
            )
          })
        ) : kanbanItems.length ? (
          kanbanItems.map((task) => (
            <div key={task.id} className="space-y-2 rounded-md border border-white/10 bg-white/[0.025] p-3">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-medium">{task.title}</p>
                  <p className="mt-1 text-sm text-muted-foreground">
                    PM needs input from {task.profile ?? task.assignee ?? 'you'} before this kanban item can continue.
                  </p>
                </div>
                <Badge tone={task.status === 'blocked' ? 'danger' : 'warning'}>{task.status}</Badge>
              </div>
              <button
                type="button"
                onClick={() => window.open(task.detail_url, '_blank', 'noopener,noreferrer')}
                className="inline-flex h-9 items-center rounded-md text-sm font-medium text-primary hover:underline"
              >
                Source task
              </button>
            </div>
          ))
        ) : (
          <EmptyState
            icon={UserRoundCheck}
            variant="bare"
            title="No action items"
            description="PM-filed info requests and blocked kanban items will appear here."
          />
        )}
      </CardContent>
    </Card>
  )
}

function GoalList({
  title,
  items,
  icon: Icon,
  tone,
}: {
  title: string
  items: GoalItem[]
  icon: typeof Square
  tone: React.ComponentProps<typeof Badge>['tone']
}) {
  return (
    <Card tone="outline">
      <CardHeader>
        <div className="flex items-center justify-between gap-3">
          <CardTitle className="flex items-center gap-2" role="heading" aria-level={2}>
            <Icon className="size-4 text-primary" />
            {title}
          </CardTitle>
          <Badge tone={tone}>{items.length}</Badge>
        </div>
      </CardHeader>
      <CardContent className="pt-4">
        {items.length > 0 ? (
          <ul className="space-y-2">
            {items.map((item) => (
              <li
                key={item.id}
                className="flex min-h-10 items-start gap-2 rounded-md border border-white/10 bg-white/[0.035] px-3 py-2 text-sm"
              >
                {item.group === 'active' ? (
                  <Square className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
                ) : (
                  <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-success" />
                )}
                <span className="min-w-0 break-words leading-6">{item.text}</span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-muted-foreground">No goals in this group.</p>
        )}
      </CardContent>
    </Card>
  )
}

export function ProjectGoalsPage() {
  const pathname = useRouterState({ select: (state) => state.location.pathname })
  if (pathname.endsWith('/provisioning') || pathname.endsWith('/slack/oauth/callback')) {
    return <Outlet />
  }

  return <ProjectGoalsContent />
}

function ProjectGoalsContent() {
  const { workspaceUuid, projectUuid } = Route.useParams()
  const search = useRouterState({ select: (state) => state.location.search })
  const project = useBosunProject(workspaceUuid, projectUuid)
  const workspace = useWorkspace(workspaceUuid)
  const goals = useBosunProjectGoals(workspaceUuid, projectUuid)
  const audit = useBosunProjectGoalsAudit(workspaceUuid, projectUuid)
  const kanban = useBosunKanbanSummary(workspaceUuid, projectUuid)
  const kanbanTasks = useBosunKanbanTasks(workspaceUuid, projectUuid)
  const updateGoals = useUpdateBosunProjectGoals(workspaceUuid, projectUuid)
  const [draft, setDraft] = React.useState<string | null>(null)

  const grouped = React.useMemo(() => parseGoals(goals.data?.body_markdown ?? ''), [goals.data])
  const canEdit = workspace.data ? canManageProjects(workspace.data.role) : false
  const editorValue = draft ?? goals.data?.body_markdown ?? ''
  const dirty = goals.data ? draft !== null && draft !== goals.data.body_markdown : false
  const progress = project.data ? projectProgress(project.data.status, kanban.data) : 0
  const remainingTasks = React.useMemo(() => remainingActiveTasks(kanban.data), [kanban.data])
  const activeTab = React.useMemo(() => activeTabFromSearch(search), [search])
  const selectedTaskId = React.useMemo(() => searchValue(search, 'task'), [search])
  const selectedTask = React.useMemo(() => {
    if (!selectedTaskId) return null
    const tasks = kanbanTasks.data ?? [
      ...(kanban.data?.active_tasks ?? []),
      ...(kanban.data?.running_tasks ?? []),
      ...(kanban.data?.blocked_tasks ?? []),
      ...(kanban.data?.waiting_tasks ?? []),
    ]
    return tasks.find((task) => task.id === selectedTaskId) ?? null
  }, [kanban.data, kanbanTasks.data, selectedTaskId])

  async function saveGoals() {
    if (!goals.data) return
    await updateGoals.mutateAsync({
      body_markdown: editorValue,
      expected_revision: goals.data.revision,
    })
    setDraft(null)
  }

  return (
    <div className="mx-auto w-full max-w-7xl space-y-6 px-4 py-6 md:px-8 md:py-8">
      <Button asChild variant="ghost" size="sm">
        <Link to="/projects">
          <ArrowLeft />
          Projects
        </Link>
      </Button>

      {project.isLoading || goals.isLoading || workspace.isLoading ? (
        <Skeleton className="h-72 w-full" />
      ) : project.error || goals.error || workspace.error ? (
        <Alert tone="danger">
          <AlertDescription>
            {formatError(project.error ?? goals.error ?? workspace.error)}
          </AlertDescription>
        </Alert>
      ) : project.data && goals.data ? (
        <>
          <section className="rounded-lg border border-white/10 bg-white/[0.035] p-5 shadow-floating backdrop-blur-xl md:p-7">
            <PageHeader
              eyebrow={workspace.data?.name ?? 'Project'}
              title={project.data.name}
              description={
                project.data.description ||
                'Project cockpit for launch readiness, PM coordination, kanban progress, action items, and release evidence.'
              }
              actions={
                <div className="flex flex-wrap gap-2">
                  <Badge tone={project.data.status === 'ready' ? 'success' : 'info'}>
                    {project.data.status}
                  </Badge>
                  {project.data.archive_status !== 'active' ? (
                    <Badge tone="warning">{project.data.archive_status}</Badge>
                  ) : null}
                </div>
              }
            />
            <div className="mt-5 grid gap-3 lg:grid-cols-[minmax(0,1fr)_28rem]">
              <div className="space-y-2">
                <div className="flex items-center justify-between gap-3 text-sm">
                  <span className="text-muted-foreground">Overall launch and delivery progress</span>
                  <span className="font-medium">{progress}%</span>
                </div>
                <Progress percent={progress} tone={project.data.status === 'ready' ? 'success' : (kanban.data?.blocked_tasks.length ?? 0) > 0 ? 'warning' : 'primary'} />
              </div>
              <div className="flex flex-wrap gap-2">
                <Badge tone="info">Active goals {goals.data.goal_counts.active}</Badge>
                <Badge tone="success">Completed {goals.data.goal_counts.completed}</Badge>
                <Badge tone="warning">Waiting {kanban.data?.waiting_tasks.length ?? 0}</Badge>
                <Badge tone={(kanban.data?.blocked_tasks.length ?? 0) > 0 ? 'danger' : 'success'}>
                  Blocked {kanban.data?.blocked_tasks.length ?? 0}
                </Badge>
              </div>
            </div>
            <div className="mt-5">
              <ProjectCockpitNav workspaceUuid={workspaceUuid} projectUuid={projectUuid} active={activeTab} />
            </div>
          </section>

          {kanban.error ? (
            <Alert tone="warning">
              <AlertDescription>Kanban summary is temporarily unavailable: {formatError(kanban.error)}</AlertDescription>
            </Alert>
          ) : null}

          <div className="space-y-4">
            {activeTab === 'overview' ? (
            <section id="overview" className="space-y-4" aria-labelledby="project-overview-heading">
              <h2 id="project-overview-heading" className="sr-only">Overview</h2>
              <section className="grid gap-3 lg:grid-cols-3">
                <GoalList title="Active" items={grouped.active} icon={Square} tone="info" />
                <GoalList title="Completed" items={grouped.completed} icon={CheckCircle2} tone="success" />
                <GoalList title="Release-reviewed" items={grouped['release-reviewed']} icon={Tags} tone="warning" />
              </section>

              <section className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_22rem]">
                <Card>
                  <CardHeader>
                    <SectionHeader
                      title="Goals document"
                      actions={
                        canEdit ? (
                          <Button
                            size="sm"
                            onClick={() => void saveGoals()}
                            disabled={!dirty || updateGoals.isPending}
                          >
                            <Save />
                            {updateGoals.isPending ? 'Saving' : 'Save'}
                          </Button>
                        ) : undefined
                      }
                    />
                  </CardHeader>
                  <CardContent className="space-y-3 pt-4">
                    {updateGoals.error ? (
                      <Alert tone="danger">
                        <AlertDescription>{formatError(updateGoals.error)}</AlertDescription>
                      </Alert>
                    ) : null}
                    <div className="space-y-2">
                      <Label htmlFor="goals-markdown">Checklist markdown</Label>
                      <Textarea
                        id="goals-markdown"
                        value={editorValue}
                        onChange={(event) => setDraft(event.target.value)}
                        disabled={!canEdit}
                        className="min-h-72 font-mono text-sm leading-6"
                      />
                    </div>
                  </CardContent>
                </Card>

                <Card tone="outline">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <ClipboardCheck className="size-4 text-primary" />
                      Audit
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3 pt-4">
                    {audit.data && audit.data.length > 0 ? (
                      audit.data.slice(0, 5).map((entry) => (
                        <div key={entry.uuid} className="rounded-md border border-white/10 p-3 text-sm">
                          <p className="font-medium">{entry.actor_email ?? 'System'}</p>
                          <p className="text-muted-foreground">
                            Revision {auditRevision(entry.audit_metadata)} ·{' '}
                            {formatLocalDateTime(entry.created_at)}
                          </p>
                        </div>
                      ))
                    ) : (
                      <p className="text-sm text-muted-foreground">No goal edits recorded yet.</p>
                    )}
                  </CardContent>
                </Card>
              </section>
            </section>
            ) : null}

            {activeTab === 'timeline' ? (
            <section id="timeline" aria-labelledby="project-timeline-heading">
              <h2 id="project-timeline-heading" className="sr-only">Timeline</h2>
              <TimelinePanel project={project.data} summary={kanban.data} />
            </section>
            ) : null}

            {activeTab === 'settings' ? (
            <section id="settings" aria-labelledby="project-settings-heading">
              <h2 id="project-settings-heading" className="sr-only">Settings</h2>
              <SlackInviteSettingsPanel project={project.data} canEdit={canEdit} />
            </section>
            ) : null}

            {activeTab === 'kanban' ? (
            <section id="kanban" aria-labelledby="project-kanban-heading" className="space-y-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <SectionHeader title="Kanban monitoring" />
                <div className="flex flex-wrap gap-2">
                  <Badge tone="info">Running {kanban.data?.running_tasks.length ?? 0}</Badge>
                  <Badge tone="danger">Blocked {kanban.data?.blocked_tasks.length ?? 0}</Badge>
                  <Badge tone="warning">Waiting {kanban.data?.waiting_tasks.length ?? 0}</Badge>
                  <Badge tone="neutral">Total {kanban.data?.counts.total ?? 0}</Badge>
                </div>
              </div>
              <h2 id="project-kanban-heading" className="sr-only">Kanban monitoring</h2>
              {selectedTaskId && !selectedTask ? (
                <Alert tone="warning">
                  <AlertDescription>Ticket {selectedTaskId} is not present in the latest kanban snapshot yet.</AlertDescription>
                </Alert>
              ) : null}
              <div className="grid gap-4 lg:grid-cols-2">
                <TaskList title="Running" tasks={kanban.data?.running_tasks ?? []} tone="info" project={project.data} />
                <TaskList title="Blocked" tasks={kanban.data?.blocked_tasks ?? []} tone="danger" project={project.data} />
                <TaskList title="Waiting" tasks={kanban.data?.waiting_tasks ?? []} tone="warning" project={project.data} />
                <TaskList title="To do" tasks={remainingTasks} tone="neutral" project={project.data} />
              </div>
              <Sheet open={Boolean(selectedTask)} modal={false}>
                <SheetContent className="w-[min(48rem,100vw)] overflow-y-auto sm:max-w-3xl">
                  <SheetHeader>
                    <SheetTitle>{selectedTask?.title ?? 'Ticket detail'}</SheetTitle>
                    <SheetDescription>
                      {selectedTask?.id ?? selectedTaskId}
                    </SheetDescription>
                  </SheetHeader>
                  <div className="px-4 pb-6">
                    {selectedTask ? <TaskDetailPanel task={selectedTask} /> : null}
                    <Button asChild variant="secondary" size="sm" className="mt-4">
                      <Link
                        to="/projects/$workspaceUuid/$projectUuid"
                        params={{ workspaceUuid, projectUuid }}
                        search={{ tab: 'kanban' }}
                      >
                        Close detail
                      </Link>
                    </Button>
                  </div>
                </SheetContent>
              </Sheet>
            </section>
            ) : null}

            {activeTab === 'pm-chat' ? (
            <section id="pm-chat" aria-labelledby="project-pm-chat-heading">
              <h2 id="project-pm-chat-heading" className="sr-only">PM Chat</h2>
              <React.Suspense fallback={<Skeleton className="h-96 w-full" />}>
                <LazyPmChatPanel workspaceUuid={workspaceUuid} projectUuid={projectUuid} summary={kanban.data} activeGoals={grouped.active} />
              </React.Suspense>
            </section>
            ) : null}

            {activeTab === 'action-items' ? (
            <section id="waiting-on-you" aria-labelledby="project-action-items-heading">
              <h2 id="project-action-items-heading" className="sr-only">Action Items</h2>
              <ActionItemsPanel workspaceUuid={workspaceUuid} projectUuid={projectUuid} summary={kanban.data} />
            </section>
            ) : null}

            {activeTab === 'releases' ? (
            <section id="releases" aria-labelledby="project-releases-heading">
              <h2 id="project-releases-heading" className="sr-only">Releases</h2>
              <ProjectReleaseNotesPanel workspaceUuid={workspaceUuid} project={project.data} />
            </section>
            ) : null}
          </div>
        </>
      ) : (
        <EmptyState
          icon={ClipboardCheck}
          title="Project not found"
          description="This project is not visible in the selected workspace."
        />
      )}
    </div>
  )
}
